import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Shield, ArrowLeft, ShieldAlert } from "lucide-react";
import { PageHeader, Loader } from "@/components/Page";
import { useAuth } from "@/context/AuthContext";
import { AllianceChat } from "@/components/alliance/AllianceChat";
import MessageBoard from "@/components/MessageBoard";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";

export default function AllianceWarRoom() {
  const { allianceId } = useParams(); // Optional: for admin viewing specific alliance
  const navigate = useNavigate();
  const { user } = useAuth();
  const [alliance, setAlliance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allianceName, setAllianceName] = useState(null);

  const isAdminView = allianceId && user?.is_admin;

  useEffect(() => {
    loadAlliance();
  }, [allianceId]);

  async function loadAlliance() {
    try {
      if (isAdminView) {
        // Admin viewing a specific alliance
        const { data } = await api.get(`/alliances/${allianceId}`);
        setAllianceName(data.alliance?.name);
        setAlliance({ alliance: { id: allianceId } }); // Minimal data needed for board
      } else {
        // Regular user viewing their own alliance
        const { data } = await api.get("/alliance");
        setAlliance(data);
      }
    } catch (err) {
      toast.error(apiErr(err));
      navigate("/alliance");
    } finally {
      setLoading(false);
    }
  }

  if (loading) return <Loader />;
  if (!alliance) return null;

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      <PageHeader
        title={
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(isAdminView ? `/alliance/${allianceId}` : "/alliance")}
              className="text-zinc-400 hover:text-amber-400 transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            {isAdminView ? <ShieldAlert size={24} className="text-red-400" /> : <Shield size={24} className="text-amber-500" />}
            <span>War Room</span>
            {isAdminView && allianceName && <span className="text-zinc-500 text-base">— {allianceName}</span>}
          </div>
        }
        subtitle={isAdminView ? "Admin read-only view" : "Strategize and communicate with your alliance!"}
      />

      {isAdminView && (
        <div className="border border-red-900/40 bg-red-950/10 p-3 mb-4 text-sm text-red-300">
          <ShieldAlert size={16} className="inline mr-2" />
          You are viewing this alliance's War Room as an admin. You have read-only access.
        </div>
      )}

      <div className="space-y-6 mt-6">
        {/* Alliance Chat - Only show for members, not admin view */}
        {!isAdminView && (
          <div>
            <AllianceChat />
          </div>
        )}

        {/* Message Board */}
        <div>
          <MessageBoard 
            apiPrefix="/alliance/board"
            allianceId={isAdminView ? allianceId : undefined}
            title="Alliance Message Board"
            emptyMessage="No threads yet. Start a discussion!"
            readOnly={isAdminView}
          />
        </div>
      </div>
    </div>
  );
}
